/**
 * Sprint TC-C.2 — Candidate roster filter bar.
 *
 * Pure controlled component. Renders a search input + a set of filter chips
 * + Boolean toggles ("Shortlisted only", "Completed only", "Released only").
 * Parent owns the filter state and applies it to the roster rows. Mounted
 * only when `variant="admin"` (the client roster stays untouched).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import {
  Search, Filter, X, Star, CheckCircle2, ShieldCheck, ChevronDown,
} from 'lucide-react';

const DECISIONS = ['shortlisted', 'continue_process', 'on_hold', 'rejected', 'hired'];
const ASSESSMENT_STATUSES = ['requested', 'in_progress', 'submitted'];
const REPORT_STATUSES = [
  'none', 'draft_ai_generated', 'under_review', 'edited_by_hm',
  'approved', 'released_to_client', 'rejected',
];

function MultiFilter({ label, options, selected, onChange, tKey, testId }) {
  const { t } = useTranslation();
  const toggle = (v) => {
    const next = selected.includes(v)
      ? selected.filter((x) => x !== v)
      : [...selected, v];
    onChange(next);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm" variant="outline"
          className="gap-1.5 h-8"
          data-testid={`${testId}-trigger`}
        >
          <Filter className="h-3 w-3" />
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-xs tabular-nums">
              {selected.length}
            </Badge>
          )}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2" data-testid={`${testId}-popover`}>
        <div className="grid gap-1">
          {options.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${
                  checked
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'bg-background hover:bg-muted/40 border-input text-foreground'
                }`}
                data-testid={`${testId}-opt-${opt}`}
              >
                <span className="capitalize truncate">
                  {t(`${tKey}.${opt}`, { defaultValue: opt.replace(/_/g, ' ') })}
                </span>
                {checked && <CheckCircle2 className="h-3 w-3" />}
              </button>
            );
          })}
          {selected.length > 0 && (
            <Button
              size="sm" variant="ghost"
              onClick={() => onChange([])}
              className="text-xs h-7 mt-1"
              data-testid={`${testId}-clear`}
            >
              <X className="h-3 w-3 mr-1" />
              {t('common.clear', { defaultValue: 'Clear' })}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ToggleChip({ active, onClick, icon: Icon, label, testId }) {
  return (
    <Button
      size="sm"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
      className="gap-1.5 h-8"
      data-testid={testId}
      aria-pressed={active}
    >
      <Icon className={`h-3.5 w-3.5 ${active ? '' : 'opacity-70'}`} />
      {label}
    </Button>
  );
}

export default function RosterFilterBar({
  filters,
  onChange,
  rowCount,
  visibleCount,
}) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  const update = (patch) => onChange({ ...filters, ...patch });
  const reset = () =>
    onChange({
      search: '',
      decisions: [],
      assessment_statuses: [],
      report_statuses: [],
      shortlisted_only: false,
      completed_only: false,
      released_only: false,
    });

  const hasActive =
    !!filters.search ||
    filters.decisions.length > 0 ||
    filters.assessment_statuses.length > 0 ||
    filters.report_statuses.length > 0 ||
    filters.shortlisted_only ||
    filters.completed_only ||
    filters.released_only;

  return (
    <div className="mb-3 space-y-2" data-testid="roster-filter-bar">
      {/* Search row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder={isES ? 'Buscar por nombre o email…' : 'Search by name or email…'}
            className="pl-8 h-8 text-sm"
            data-testid="roster-search"
          />
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">
          {isES
            ? `${visibleCount} de ${rowCount}`
            : `${visibleCount} of ${rowCount}`}
        </span>
        {hasActive && (
          <Button
            size="sm" variant="ghost" onClick={reset}
            className="gap-1.5 h-8 ml-auto"
            data-testid="roster-reset-filters"
          >
            <X className="h-3 w-3" />
            {isES ? 'Limpiar filtros' : 'Clear filters'}
          </Button>
        )}
      </div>

      {/* Chip row */}
      <div className="flex items-center gap-2 flex-wrap">
        <MultiFilter
          label={isES ? 'Decisión' : 'Decision'}
          options={DECISIONS}
          selected={filters.decisions}
          onChange={(v) => update({ decisions: v })}
          tKey="recruitment.candidates.decision"
          testId="roster-filter-decision"
        />
        <MultiFilter
          label={isES ? 'Estado evaluación' : 'Assessment'}
          options={ASSESSMENT_STATUSES}
          selected={filters.assessment_statuses}
          onChange={(v) => update({ assessment_statuses: v })}
          tKey="recruitment.candidates.assessmentStatus"
          testId="roster-filter-assessment"
        />
        <MultiFilter
          label={isES ? 'Informe' : 'Report'}
          options={REPORT_STATUSES}
          selected={filters.report_statuses}
          onChange={(v) => update({ report_statuses: v })}
          tKey="recruitment.candidates.reportStatus"
          testId="roster-filter-report"
        />
        <ToggleChip
          active={filters.shortlisted_only}
          onClick={() => update({ shortlisted_only: !filters.shortlisted_only })}
          icon={Star}
          label={isES ? 'Preseleccionados' : 'Shortlisted'}
          testId="roster-filter-shortlisted"
        />
        <ToggleChip
          active={filters.completed_only}
          onClick={() => update({ completed_only: !filters.completed_only })}
          icon={CheckCircle2}
          label={isES ? 'Completados' : 'Completed'}
          testId="roster-filter-completed"
        />
        <ToggleChip
          active={filters.released_only}
          onClick={() => update({ released_only: !filters.released_only })}
          icon={ShieldCheck}
          label={isES ? 'Entregados' : 'Released'}
          testId="roster-filter-released"
        />
      </div>
    </div>
  );
}

export const EMPTY_ROSTER_FILTERS = {
  search: '',
  decisions: [],
  assessment_statuses: [],
  report_statuses: [],
  shortlisted_only: false,
  completed_only: false,
  released_only: false,
};

/** Pure filter — returns the subset of rows matching the current filters. */
export function applyRosterFilters(rows, f) {
  if (!rows?.length) return [];
  const search = (f.search || '').trim().toLowerCase();
  return rows.filter((r) => {
    if (search) {
      const hay =
        `${r.candidate_name || ''} ${r.candidate_email || ''}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    if (f.decisions.length > 0) {
      const ds = r.decision?.status;
      if (!ds || !f.decisions.includes(ds)) return false;
    }
    if (f.assessment_statuses.length > 0) {
      if (!f.assessment_statuses.includes(r.assessment_status)) return false;
    }
    if (f.report_statuses.length > 0) {
      if (!f.report_statuses.includes(r.report_status || 'none')) return false;
    }
    if (f.shortlisted_only && !r.shortlisted) return false;
    if (f.completed_only && r.assessment_status !== 'submitted') return false;
    if (f.released_only && !r.report_released) return false;
    return true;
  });
}
